import DataCacheService from '@/shared/services/DataCacheService';
import { dynamicSortMultiple } from '@/shared/utils/dynamicSort';
import config from '@/config';
import api from '@/shared/api';
import { mapSuggestedMailAddress } from '@/shared/services/ObjectDisplayService';
import { listItemMsg } from '@/shared/components/FormattedListValue';
export var Purpose;
(function (Purpose) {
    Purpose[Purpose["Other"] = 0] = "Other";
    Purpose[Purpose["BillingAndReminder"] = 1] = "BillingAndReminder";
    Purpose[Purpose["TaskManagement"] = 2] = "TaskManagement";
    Purpose[Purpose["BillingOnly"] = 3] = "BillingOnly";
    Purpose[Purpose["RemindersOnly"] = 4] = "RemindersOnly";
})(Purpose || (Purpose = {}));
const emptyHttpArrayPromise = () => Promise.resolve({ data: [] });
export const getEmailRecipients = (relationId, invoiceStreamId, intl) => {
    const emailAddressesPromise = relationId
        ? api.base.get(`${config.appUrls.api}/api/address/MailSuggestions/${relationId}`)
        : emptyHttpArrayPromise();
    const relationEmployeesPromise = relationId
        ? api.base.get(`${config.appUrls.api}/api/Relation/Employees/${relationId}`, {
            params: {
                invoiceStreamId,
            },
        })
        : emptyHttpArrayPromise();
    return Promise.all([emailAddressesPromise, relationEmployeesPromise]).then(([{ data: emailAddresses }, { data: relationEmployees }]) => {
        const addresses = emailAddresses.reduce((result, x, idx) => {
            if (!x)
                return result;
            if (result.some(y => y.value.toLowerCase() === x.emailAddress.toLowerCase()))
                return result;
            result.push(Object.assign(Object.assign({}, x), { key: `${x.addressId}-${idx}`, value: x.emailAddress, label: mapSuggestedMailAddress(x, intl) }));
            return result;
        }, []);
        const employees = DataCacheService.getUsers()
            .map(x => {
            let label = `${x.name} (${x.email})`;
            const profiles = relationEmployees
                .filter(e => e.userId === x.id)
                .map(e => intl.formatMessage(listItemMsg('employeeprofile', e.profile)))
                .sort();
            if (profiles.length === 0)
                return {
                    key: x.id,
                    value: x.email,
                    label,
                    isRelationEmployee: false,
                    isActive: x.isActive,
                };
            return {
                key: x.id,
                value: x.email,
                label: `${label} (${profiles.join(', ')})`,
                isRelationEmployee: true,
                isActive: x.isActive,
            };
        })
            .sort(dynamicSortMultiple('-isRelationEmployee', 'label'));
        return { addresses, employees };
    });
};
export const getCommunicationSettings = (relationId, purpose) => {
    const requestPromise = relationId
        ? api.base.get(`${config.appUrls.api}/api/CommunicationSetting/get?id=${relationId}`)
        : emptyHttpArrayPromise();
    return requestPromise.then(({ data }) => {
        if (!data)
            return [];
        if (data && typeof purpose !== 'undefined') {
            return data.filter(x => x.purpose === purpose && x.medium === 1);
        }
        return data.filter(x => x.medium === 1);
    });
};
const findAddressesForType = (settings, addresses, type) => settings
    ? settings.reduce((a, e) => {
        // purpose 0 = other, medium 1 = email, type = to/cc/bcc
        if (e.purpose === 0 && e.medium === 1 && e.type === type) {
            const address = addresses.find(addr => !!addr.value && addr.addressId === e.addressId);
            if (address)
                a.push(address.value);
        }
        return a;
    }, [])
    : [];
export const createEmptyRecipients = () => ({
    to: [''],
    cc: [],
    bcc: [],
    empTo: [],
    empCc: [],
    empBcc: [],
});
export const createRecipientsFromCommunicationSettings = (settings, addresses) => {
    if (addresses.length === 0)
        return {};
    const result = {
        to: findAddressesForType(settings, addresses, 0),
        cc: findAddressesForType(settings, addresses, 1),
        bcc: findAddressesForType(settings, addresses, 2),
    };
    if (result.to.length === 0) {
        const main = addresses.find(x => !!x.value && x.linkType === null && x.isMain);
        if (main) {
            result.to = [main.value];
        }
    }
    return result;
};
export const validateEmail = (value) => {
    if (!value)
        return false;
    return /^[^@]+@.+\..+$/.test(value);
};
export const combineForRequest = (recipients) => ({
    to: [...recipients.to, ...recipients.empTo].filter(x => validateEmail(x)),
    cc: [...recipients.cc, ...recipients.empCc].filter(x => validateEmail(x)),
    bcc: [...recipients.bcc, ...recipients.empBcc].filter(x => validateEmail(x)),
});
export const validateRecipients = (recipients) => {
    if (recipients.to.length === 0 && recipients.empTo.length === 0)
        return false;
    if (recipients.to.some(x => !validateEmail(x)))
        return false;
    if (recipients.cc.some(x => !validateEmail(x)))
        return false;
    if (recipients.bcc.some(x => !validateEmail(x)))
        return false;
    if (recipients.empTo.some(x => !validateEmail(x)))
        return false;
    if (recipients.empCc.some(x => !validateEmail(x)))
        return false;
    if (recipients.empBcc.some(x => !validateEmail(x)))
        return false;
    return true;
};
export const sanitizeRecipients = (recipients) => {
    const result = Object.assign(Object.assign({}, createEmptyRecipients()), recipients);
    if (!result.to)
        result.to = [''];
    if (!result.cc)
        result.cc = [];
    if (!result.bcc)
        result.bcc = [];
    if (!result.empTo)
        result.empTo = [];
    if (!result.empCc)
        result.empCc = [];
    if (!result.empBcc)
        result.empBcc = [];
    if (result.to.length === 0 && result.empTo.length === 0) {
        result.to = [''];
    }
    return result;
};
